import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { findCameraBySlug } from '../utils/slug';
import VideoPlayer from '../components/VideoPlayer';
import WeatherView from '../components/WeatherView';
import './CameraView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function CameraView() {
  const { cameraSlug } = useParams();
  const navigate = useNavigate();
  const [camera, setCamera] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const streamIdRef = useRef(null);
  const cameraNameRef = useRef(null);

  useEffect(() => {
    // We'll find the camera by slug, so we don't need to decode
    let isMounted = true;
    let heartbeatInterval = null;
    
        // Handle page visibility - keep watching even when tab is hidden
        const handleVisibilityChange = () => {
          if (!document.hidden && socketRef.current?.connected && streamIdRef.current && cameraNameRef.current) {
            // Page is visible again, make sure we're still registered
            socketRef.current.emit('watch-stream', { cameraName: cameraNameRef.current });
          }
        };
    
    // Connect to Socket.IO with reconnection enabled
    const socket = io(API_BASE_URL, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });
    socketRef.current = socket;

    const fetchCameraAndStartStream = async () => {
      try {
        // Fetch all cameras to find the one we want by slug
        const response = await fetch(`${API_BASE_URL}/api/cameras`);
        if (!response.ok) {
          throw new Error('Failed to fetch cameras');
        }
        const cameras = await response.json();
        const foundCamera = findCameraBySlug(cameras.filter(cam => cam.enabled), cameraSlug);
        
        if (!foundCamera) {
          throw new Error('Camera not found');
        }
        
        if (!isMounted) return;
        setCamera(foundCamera);
        cameraNameRef.current = foundCamera.name;

        // Listen for stream ready - only update if URL actually changed
        socket.on('stream-ready', (data) => {
          if (!isMounted) return;
          console.log('Stream ready:', data);
          streamIdRef.current = data.streamId;
          const url = data.fullUrl || data.hlsUrl;
          // Only update state if URL changed to avoid unnecessary re-renders
          setStreamUrl(prevUrl => {
            if (prevUrl !== url) {
              console.log('Setting stream URL:', url);
              return url;
            }
            console.log('Stream URL unchanged, skipping update');
            return prevUrl;
          });
          setLoading(false);
          setError(null);
        });

        // Listen for errors
        socket.on('error', (data) => {
          if (!isMounted) return;
          console.error('Socket error:', data);
          setError(data.message);
          setLoading(false);
        });

        // Handle reconnection - re-register as viewer only on reconnect (not initial connect)
        let hasInitiallyConnected = false;
        
        socket.on('connect', () => {
          console.log('Socket connected');
          // Only re-register on reconnect, not initial connect
          // Initial connect is handled by the explicit emit below
          if (hasInitiallyConnected && cameraNameRef.current) {
            console.log('Reconnected, re-registering as viewer');
            socket.emit('watch-stream', { cameraName: cameraNameRef.current });
          }
          hasInitiallyConnected = true;
        });
        
        socket.on('reconnect', (attemptNumber) => {
          console.log(`Socket reconnected (attempt ${attemptNumber})`);
          // The 'connect' event will also fire, so we don't need to emit here
        });

        // Handle disconnect - don't stop watching, just wait for reconnect
        socket.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          // Don't emit stop-watching - let reconnection handle it
          // The socket will automatically reconnect and re-register
        });

        // Send periodic heartbeat to keep connection alive (ping only, don't re-register)
        heartbeatInterval = setInterval(() => {
          if (socket.connected) {
            // Just ping to keep connection alive - don't re-register
            socket.emit('ping');
          }
        }, 30000); // Every 30 seconds

        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Request to watch the stream
        socket.emit('watch-stream', { cameraName: cameraNameRef.current });
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading camera:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCameraAndStartStream();

    // Cleanup on unmount - ONLY when component is actually unmounting
    return () => {
      isMounted = false;
      // Clear heartbeat interval
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      // Remove visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (socketRef.current) {
        // Only stop watching if we're actually leaving the page
        // This cleanup only runs when the component unmounts (user navigates away)
        if (streamIdRef.current) {
          console.log('Component unmounting, stopping watch');
          socketRef.current.emit('stop-watching', { streamId: streamIdRef.current });
        }
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [cameraSlug]);

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Camera stream laden...</p>
        </div>
      </div>
    );
  }

  if (error || !camera) {
    return (
      <div className="app-container">
        <div className="error-screen">
          <h2>⚠️ Fout</h2>
          <p>{error || 'Camera niet gevonden'}</p>
          <button type="button" onClick={() => navigate('/')} className="back-button">
            ← Terug naar Camera Lijst
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <Link to="/" className="back-link">← Terug naar Camera Lijst</Link>
        <h1>{camera.name}</h1>
        <p className="subtitle">Het kan even duren voordat de camera online komt, ook als je al een beeld ziet.</p>
      </header>

      <div className="camera-view-container">
        <VideoPlayer camera={camera} streamUrl={streamUrl} />
        <WeatherView />
      </div>
    </div>
  );
}

export default CameraView;
    